import { Injectable, inject } from '@angular/core';

import { FixturePage, Result } from '@cds/betting-offer';
import { OfferMapping, ScoreboardMode } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { ExternalPriceFormatterService } from '@frontend/sports/odds/feature/native-price';
import { UserService } from '@frontend/sports/user/feature';
import { WINDOW } from '@frontend/vanilla/core';
import { Observable, catchError, map, of } from 'rxjs';

import { EnhancedTeaserViewModel } from './teaser.models';

@Injectable({ providedIn: 'root' })
export class TeaserUtilsService {
    private teaserSizes = [320, 480, 540, 640, 720, 1080];
    private twoOptionPrefix = ['1', '2'];
    private threeOptionPrefix = ['1', 'X', '2'];
    private readonly logger: SportsRemoteLogger;

    constructor(
        private userService: UserService,
        loggerFactory: LoggerFactory,
        private externalPriceFormatter: ExternalPriceFormatterService,
        private bettingOffer: BettingOfferApi,
    ) {
        this.logger = loggerFactory.getLogger('TeaserUtilsService');
    }

    filter(teasers: EnhancedTeaserViewModel[]): EnhancedTeaserViewModel[] {
        if (teasers && teasers.filter) {
            return teasers.filter((teaser) => {
                if (teaser.isAuthenticated && !this.userService.isAuthenticated) {
                    return false;
                }

                return !!teaser.imageUrl;
            });
        }

        return [];
    }

    extend(teasers: EnhancedTeaserViewModel[], screenWidth: number): Observable<EnhancedTeaserViewModel[]> {
        const eventsId = teasers.filter((t) => t.event).map((t) => t.event!.id);
        const size = this.getTeaserSize(screenWidth);

        let eventsData: Observable<FixturePage | undefined> = of(undefined);

        if (eventsId.length) {
            eventsData = this.bettingOffer.getFixtureList({
                fixtureIds: eventsId.join(','),
                scoreboardMode: ScoreboardMode.None,
                offerMapping: OfferMapping.MainMarkets,
            });
        }

        return eventsData.pipe(
            map((response) => this.process(teasers, response)),
            catchError((error) => {
                this.logError(error);

                return of(teasers);
            }),
            map((list) => this.applyDetail(list, size)),
        );
    }

    private getTeaserSize(screenWidth: number): number {
        if (this.#window.devicePixelRatio && this.#window.devicePixelRatio > 2) {
            // if the CSS pixel ratio is greater than 2, we double the requested width to reduce the gap between the requested width and the actual device pixels.
            // Without this adjustment, the image will appear too unsharp/blurry on devices with a small display but super high resolution.
            screenWidth *= 2;
        }

        return this.teaserSizes.find((x) => x >= screenWidth) || this.teaserSizes[this.teaserSizes.length - 1];
    }

    private process(teasers: EnhancedTeaserViewModel[], response: FixturePage | undefined): EnhancedTeaserViewModel[] {
        if (!response?.fixtures.length) {
            return teasers;
        }

        const findFixture = (event?: { id: string }) => response.fixtures.find((current) => !!event && current.id === event.id);

        return teasers
            .map((teaser) => ({ teaser, fixture: findFixture(teaser.event) }))
            .filter(({ teaser, fixture }) => !teaser.event || fixture)
            .map(({ teaser, fixture }) => {
                if (!fixture) {
                    return teaser;
                }

                const market = fixture.games.pop();
                const options = market ? market.results : [];

                return Object.assign(teaser, {
                    details: {
                        league: { name: fixture.competition.name.value },
                        sports: { name: fixture.sport.name.value },
                    },
                    showMore: true,
                    options: this.buildTeaserOptionText(options),
                });
            });
    }

    private logError(error: Error): void {
        this.logger.error(error, 'Unable to fetch teasers');
    }

    private buildTeaserOptionText(options: Result[]): string[] {
        if (!options.length) {
            return [];
        }
        const prefixes = options.length === 3 ? this.threeOptionPrefix : this.twoOptionPrefix;

        return options.map((option, index) => `${prefixes[index]}-${this.externalPriceFormatter.format(option)}`);
    }

    private applyDetail(teasers: EnhancedTeaserViewModel[], size: number): EnhancedTeaserViewModel[] {
        return teasers.map((teaser) => ({
            ...teaser,
            subtitle: this.getSubtitle(teaser),
            imageUrl: `${teaser.imageUrl}?p=width${size}`,
            showMore: teaser.href ? true : teaser.showMore,
        }));
    }

    private getSubtitle(teaser: EnhancedTeaserViewModel): string {
        if (teaser.displayCountryLeague && teaser.event && teaser.details) {
            return teaser.details.sports.name + ' | ' + teaser.details.league.name;
        }

        return teaser.subtitle;
    }

    readonly #window = inject(WINDOW);
}
